// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useState, useEffect } from 'react';
import { Modal, TextArea as FinTextArea, InputLabel, DateTimeInput, StatusBanner } from '@loa/ui-components';

const MIN_REASON_LENGTH = 5;
const MAX_REASON_LENGTH = 1000;

type PolicyMetadataChangeReasonModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  onConfirm: (changeReason: string, asOfDateTime: string) => Promise<void>;
  sectionLabel: string;
  policyNumber: string;
}>;

function nowLocalISO(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

export function PolicyMetadataChangeReasonModal({
  open,
  onClose,
  onConfirm,
  sectionLabel,
  policyNumber,
}: PolicyMetadataChangeReasonModalProps) {
  const [changeReason, setChangeReason] = useState('');
  const [asOfDateTime, setAsOfDateTime] = useState(nowLocalISO());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setChangeReason('');
      setAsOfDateTime(nowLocalISO());
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const trimmedReason = changeReason.trim();
  const isConfirmDisabled =
    trimmedReason.length < MIN_REASON_LENGTH ||
    trimmedReason.length > MAX_REASON_LENGTH ||
    !asOfDateTime ||
    submitting;

  const handleConfirm = async () => {
    if (trimmedReason.length < MIN_REASON_LENGTH) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(trimmedReason, asOfDateTime);
      onClose();
    } catch {
      setError('Failed to save changes. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_REASON_LENGTH) {
      setChangeReason(text);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="medium">
      <Modal.Header icon="edit" title={`Update ${sectionLabel.toLowerCase()}`} onClose={onClose} />
      <Modal.Content>
        <div className="flex flex-col gap-lg">
          <div className="text-[#000] font-sans text-body-02 font-medium leading-normal">
            You are about to update the <strong>{sectionLabel.toLowerCase()}</strong> for policy number{' '}
            <strong>{policyNumber}</strong>. Please give a reason for this change.
          </div>

          <div>
            <InputLabel htmlFor="changeReason" required>Change reason</InputLabel>
            <FinTextArea
              id="changeReason"
              placeholder="Describe the reason for this change..."
              value={changeReason}
              onChange={handleReasonChange}
              disabled={submitting}
              rows={4}
              fluid
            />
          </div>

          <DateTimeInput
            label="As of"
            id="asOfDateTime"
            mode="datetime"
            value={asOfDateTime}
            onChange={setAsOfDateTime}
            disabled={submitting}
            required
            helperText="The date and time when this information became effective."
          />

          <div className="text-[#000] font-sans text-body-02 font-normal leading-normal">
            This change will be added to the log for this policy.
          </div>

          {error && (
            <StatusBanner
              variant="error"
              message={error}
              visible
              position="inline"
              dismissible={false}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: submitting }}
        primaryAction={{ label: 'Save Changes', onClick: () => { void handleConfirm(); }, disabled: isConfirmDisabled, loading: submitting }}
      />
    </Modal>
  );
}
